const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"
export const API_URL = API_BASE_URL

export interface Category {
    id: number
    name: string
    slug: string
    description: string
    image: string | null
    image_url: string | null
    products_count: number
    created_at: string
}

export interface Product {
    id: number
    name: string
    slug: string
    description: string
    price: string
    image: string
    image_url: string | null
    stock: number
    available: boolean
    category: number
    category_name: string
    created_at: string
    updated_at: string
}

export interface HeroSlide {
    id: number
    title: string
    subtitle: string
    image: string
    image_url: string | null
    button_text: string
    button_link: string
    order: number
}

export interface Order {
    id: number
    product: number
    product_name: string
    customer_name: string
    phone_number: string
    quantity: number
    total_price: string
    status: string
    note: string
    created_at: string
}

export interface CreateOrderData {
    product: number
    customer_name: string
    phone_number: string
    quantity: number
    note?: string
}

export interface Language {
    id: number
    code: string
    name: string
    is_default: boolean
    is_active: boolean
}

export interface Translations {
    [key: string]: string
}

export interface AllTranslations {
    [page: string]: Translations
}

export interface SiteSettings {
    site_name: string
    site_description: string
    logo_url: string | null
    email: string
    phone: string
    address: string
}

interface PaginatedResponse<T> {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

async function fetchFromAPI<T>(url: string): Promise<T[]> {
    try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        const response = await fetch(url, {
            cache: "no-store",
            signal: controller.signal,
            headers: {
                Accept: "application/json",
            },
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
            return []
        }

        const data = await response.json()

        if (data && typeof data === "object" && "results" in data && Array.isArray(data.results)) {
            return data.results as T[]
        }

        if (Array.isArray(data)) {
            return data as T[]
        }

        return []
    } catch {
        return []
    }
}

async function fetchSingleFromAPI<T>(url: string): Promise<T | null> {
    try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        const response = await fetch(url, {
            cache: "no-store",
            signal: controller.signal,
            headers: {
                Accept: "application/json",
            },
        })

        clearTimeout(timeoutId)

        if (!response.ok) return null

        const data = await response.json()
        return data as T
    } catch {
        return null
    }
}

export const api = {
    // Categories
    getCategories: async (): Promise<Category[]> => {
        return fetchFromAPI(`${API_BASE_URL}/categories/`)
    },

    getCategoryBySlug: async (slug: string): Promise<Category | null> => {
        return fetchSingleFromAPI(`${API_BASE_URL}/categories/${slug}/`)
    },

    // Products
    getProducts: async (params?: {
        category?: number
        search?: string
        ordering?: string
    }): Promise<Product[]> => {
        const queryParams = new URLSearchParams()
        if (params?.category) queryParams.append("category", params.category.toString())
        if (params?.search) queryParams.append("search", params.search)
        if (params?.ordering) queryParams.append("ordering", params.ordering)

        const url = `${API_BASE_URL}/products/${queryParams.toString() ? "?" + queryParams.toString() : ""}`
        return fetchFromAPI(url)
    },

    getFeaturedProducts: async (): Promise<Product[]> => {
        return fetchFromAPI(`${API_BASE_URL}/products/featured/`)
    },

    getProduct: async (idOrSlug: string): Promise<Product | null> => {
        return fetchSingleFromAPI(`${API_BASE_URL}/products/${idOrSlug}/`)
    },

    getProductBySlug: async (slug: string): Promise<Product | null> => {
        return fetchSingleFromAPI(`${API_BASE_URL}/products/${slug}/`)
    },

    // Hero Slides
    getHeroSlides: async (): Promise<HeroSlide[]> => {
        return fetchFromAPI(`${API_BASE_URL}/hero-slides/`)
    },

    // Orders
    createOrder: async (orderData: CreateOrderData): Promise<{ success: boolean; order?: Order; error?: string }> => {
        try {
            console.log("[v0] Creating order with data:", orderData)
            const response = await fetch(`${API_BASE_URL}/orders/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(orderData),
            })

            console.log("[v0] Order response status:", response.status)

            if (!response.ok) {
                const errorData = await response.json()
                console.log("[v0] Order error data:", errorData)
                return { success: false, error: errorData.error || "Buyurtma yaratishda xatolik" }
            }

            const order = await response.json()
            console.log("[v0] Order created successfully:", order)
            return { success: true, order }
        } catch (error) {
            console.log("[v0] Order creation error:", error)
            return { success: false, error: "Server bilan bog'lanishda xatolik" }
        }
    },

    // Languages
    getLanguages: async (): Promise<Language[]> => {
        return fetchFromAPI(`${API_BASE_URL}/languages/`)
    },

    // Translations
    getTranslations: async (page: string, lang = "uz"): Promise<Translations> => {
        try {
            const response = await fetch(`${API_BASE_URL}/translations/page/${page}/?lang=${lang}`, {
                cache: "no-store",
            })
            if (!response.ok) return {}
            return await response.json()
        } catch {
            return {}
        }
    },

    getAllTranslations: async (lang = "uz"): Promise<AllTranslations> => {
        try {
            const response = await fetch(`${API_BASE_URL}/translations/all/?lang=${lang}`, {
                cache: "no-store",
            })
            if (!response.ok) return {}
            return await response.json()
        } catch {
            return {}
        }
    },

    // Site Settings
    getSiteSettings: async (): Promise<SiteSettings | null> => {
        return fetchSingleFromAPI(`${API_BASE_URL}/site-settings/`)
    },

    sendContact: async (data: { name: string; phone: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            console.log("[v0] Sending contact with data:", data)
            const response = await fetch(`${API_BASE_URL}/contact/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(data),
            })

            console.log("[v0] Contact response status:", response.status)

            if (!response.ok) {
                const errorData = await response.json()
                console.log("[v0] Contact error data:", errorData)
                return { success: false, error: errorData.error || "Xabar yuborishda xatolik" }
            }

            console.log("[v0] Contact sent successfully")
            return { success: true }
        } catch (error) {
            console.log("[v0] Contact form error:", error)
            return { success: false, error: "Server bilan bog'lanishda xatolik" }
        }
    },
}

export const getProducts = api.getProducts
export const getProduct = api.getProduct
export const getCategories = api.getCategories
export const createOrder = api.createOrder
export const getSiteSettings = api.getSiteSettings
export const getAllTranslations = api.getAllTranslations
export const sendContact = api.sendContact
